import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

interface AuthorBioProps {
  name?: string;
  title?: string;
  credentials?: string[];
  bio?: string;
  expertise?: string[];
  showFullBio?: boolean;
}

const DEFAULT_AUTHOR = {
  name: "The PEO App Editorial Team",
  title: "HR Compliance & PEO Specialists",
  credentials: [
    "SHRM-CP Certified",
    "PHR Certified", 
    "15+ Years PEO Industry Experience",
    "Former HR Directors & Compliance Officers"
  ],
  bio: "Our editorial team consists of seasoned HR professionals, former PEO executives, and compliance specialists with decades of combined experience in the Professional Employer Organization industry. We maintain strict editorial independence and provide unbiased guidance to help businesses find the right PEO partner.",
  expertise: [
    "PEO Compliance & Regulations",
    "Multi-State Employment Law",
    "Benefits Administration",
    "Workers' Compensation",
    "Payroll & Tax Compliance",
    "HR Technology Platforms"
  ]
};

export const AuthorBio = ({ 
  name = DEFAULT_AUTHOR.name,
  title = DEFAULT_AUTHOR.title,
  credentials = DEFAULT_AUTHOR.credentials,
  bio = DEFAULT_AUTHOR.bio,
  expertise = DEFAULT_AUTHOR.expertise,
  showFullBio = true
}: AuthorBioProps) => {
  return (
    <Card className="bg-muted/30 border-l-4 border-l-primary">
      <CardContent className="pt-6">
        <div className="space-y-4">
          {/* Author Header */}
          <div>
            <h3 className="text-lg font-semibold text-foreground">{name}</h3>
            <p className="text-sm text-muted-foreground font-medium">{title}</p>
          </div>

          {/* Credentials */}
          <div className="flex flex-wrap gap-2">
            {credentials.map((credential, index) => (
              <Badge key={index} variant="secondary" className="text-xs">
                {credential}
              </Badge>
            ))}
          </div>

          {/* Bio */}
          {showFullBio && (
            <p className="text-sm text-muted-foreground leading-relaxed">
              {bio}
            </p>
          )}

          {/* Areas of Expertise */}
          <div>
            <h4 className="text-sm font-medium text-foreground mb-2">Areas of Expertise:</h4>
            <div className="flex flex-wrap gap-1">
              {expertise.map((area, index) => (
                <Badge key={index} variant="outline" className="text-xs">
                  {area}
                </Badge>
              ))}
            </div>
          </div>

          {/* Editorial Note */}
          <div className="text-xs text-muted-foreground pt-2 border-t border-border/50">
            <p>
              <strong>Editorial Independence:</strong> Our recommendations are based on extensive research and industry expertise. 
              We maintain strict editorial standards and do not favor any particular PEO provider.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};